import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {StateParams} from "../../common";
import {LineCounts} from "../../models/counts/lineCounts";
import {apsEnums} from "../../shared/dto/aps.enums";
import {NavigationModel, Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsDisplayEnums} from "../../shared/dto/enums";
import {States} from "../../shared/config/constant.module";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import CountsServiceName, {CountsService} from "../../services/counts/counts.service";
import GroupRejectsServiceName, {GroupRejectsService} from "../../services/counts/groupRejects.service";
import template from './summary.tpl.html';

let requires = [LoaderServiceName, NavigationServiceName, CountsServiceName, GroupRejectsServiceName];

interface CountSummaryParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  autoRun: boolean;
}

interface SummaryData {
  sections: SummarySection[];
}

interface SummarySection {
  heading: string;
  columns: string[];
  rows: SummaryRow[];
}

interface SummaryRow {
  name: string;
  results: string[];
}

interface SummaryController {
  summaryData: SummaryData;
  lineCounts: LineCounts;
  interval: apsDisplayEnums.CountDisplayInterval;
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  form: angular.IFormController;
  generateReport(): void;
  updatePlant(): void;
  viewGroupRejects(dimpleEvent: {xValue: string}): void;
}

let commonColumns = ['Last 24 Hours', 'Current Hour', 'Previous Hour', 'Current Shift', 'Shift -1', 'Shift -2', 'Rolling Hour'];

export default angular.module('aps.controller.countSummary', requires)
  .config(countSummaryConfig)
  .controller('CountSummaryCtrl', countSummaryCtrl)
  .name;

function countSummaryConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.COUNT_SUMMARY,
    url: '/countSummary?autoRun&reportPlantId&reportShopId',
    params: {
      autoRun: {dynamic: true},
      reportPlantId: {dynamic: true},
      reportShopId: {dynamic: true},
    },
    controller: 'CountSummaryCtrl as vm',
    template: template,
    data: {
      pageTitle: 'Production Summary'
    }
  });
}

function countSummaryCtrl(
  loaderService: LoaderService,
  navigationService: NavigationService,
  countsService: CountsService,
  groupRejectsService: GroupRejectsService,
  $q: angular.IQService,
  $state: angular.ui.IStateService,
  $stateParams: CountSummaryParams
): SummaryController {
  let vm = {} as SummaryController;

  function getOrCreateRow(name: string, section: SummarySection) {
    let row = _.find(section.rows, {name: name});
    if (row) {
      return row;
    }

    row = {
      name: name,
      results: []
    };
    section.rows.push(row);
    return row;
  }

  function completeDataProcess(data: SummaryData, model: LineCounts) {
    vm.summaryData = data;
    vm.lineCounts = model;
  }

  function processCavityData(data: SummaryData,
                             model: LineCounts,
                             detail: apsViewModels.CavityDetail[]) {
    if (detail.length > 5) {
      detail = detail.slice(0, 5);
    }

    data.sections[3].rows = detail.map(d => ({
      name: d.defect,
      results: [
        d.mold < 0 ? 'UNR' : d.mold.toString(10),
        d.cavity,
        d.rejectPercent.toFixed(1)
      ]
    }));

    completeDataProcess(data, model);
  }

  function processData(model: LineCounts): angular.IPromise<any> {
    let data = {
      sections: [{
        heading: 'Performance',
        columns: [''],
        rows: []
      }, {
        heading: 'Losses (% of Shear Cuts)',
        columns: [''],
        rows: []
      }, {
        heading: 'All Inspection Devices',
        columns: ['Top Losses (% of Inspected)'],
        rows: []
      }]
    } as SummaryData;
    data.sections.forEach(s => s.columns.push(...commonColumns));
    data.sections.push({
      heading: 'Cavity Reading Inspection Devices',
      columns: ['Top Defects (% of Inspected)', 'Mld', 'Cav', '10 Mins'],
      rows: []
    });

    model.handleIntervalChange(apsDisplayEnums.CountDisplayInterval.PreviousHour);
    let groups = model.equipmentGroups.filter(g => g.hasRejects);
    groups.sort((a, b) => parseFloat(b.rejectCount.percent)
      - parseFloat(a.rejectCount.percent));
    if (groups.length > 5) {
      groups = groups.slice(0, 5);
    }

    let interval = apsDisplayEnums.CountDisplayInterval.Last24Hours;
    while (apsDisplayEnums.CountDisplayInterval[interval]) {
      model.handleIntervalChange(interval);

      // performance
      let section = data.sections[0];
      if (model.gobsProduced) {
        let speed = getOrCreateRow('Speed', section);
        speed.results.push(model.gobsProduced.speed);
      }
      if (model.packedCount) {
        let packedCount = getOrCreateRow('Packed', section);
        packedCount.results.push(model.packedCount.count.toString(10));
      }
      let heEff = _.find(model.kpis, {efficiencyName: 'H/E Efficiency'});
      if (heEff) {
        let heRow = getOrCreateRow(heEff.efficiencyName, section);
        heRow.results.push(heEff.efficiency);
      }
      let packEff = _.find(model.kpis, {efficiencyName: 'Pack Efficiency'});
      if (packEff) {
        let packRow = getOrCreateRow(packEff.efficiencyName, section);
        packRow.results.push(packEff.efficiency);
      }

      // losses
      section = data.sections[1];
      if (heEff) {
        let heLoss = getOrCreateRow('H/E Losses', section);
        heLoss.results.push(heEff.lossPercent);
      }
      let ceEff = _.find(model.kpis, {efficiencyName: 'C/E Efficiency'});
      if (ceEff && model.gobsProduced) {
        let ceLoss = getOrCreateRow('C/E Inspection Equip Rejects', section);
        let ceLossPercent = 'N/A';
        if (model.gobsProduced.count > 0) {
          ceLossPercent = ((ceEff.losses / model.gobsProduced.count) * 100).toFixed(1) + '%';
        }
        ceLoss.results.push(ceLossPercent);
      }

      // inspection devices
      section = data.sections[2];
      for (let i = 0; i < groups.length; i++) {
        let group = groups[i];
        if (!group.hasRejects) {
          continue;
        }

        let row = getOrCreateRow(group.name, section);
        row.results.push(group.rejectCount.percent);
      }

      interval++;
    }
    model.handleIntervalChange(vm.interval);

    // cavity devices
    let cdHeye = _.find(model.equipmentGroups, g =>
      _.some(g.types, t => t === apsEnums.LineEquipmentType.CheckDetector
        || t === apsEnums.LineEquipmentType.HEYE));
    if (cdHeye) {
      let now = moment().startOf('minute');
      if (now.minute() % 10 !== 0) {
        now = now.subtract(now.minute() % 10, 'minute', );
      }
      let endTime = now.toDate();
      let startTime = now.subtract(10, 'minute').toDate();

      return countsService.getCavityCounts(vm.selectedShop.id, cdHeye.types,
        startTime, endTime).then(detail => processCavityData(data, model, detail));
    }

    completeDataProcess(data, model);
    return $q.when();
  }

  function runReport(): angular.IPromise<any> {
    vm.summaryData = null;
    vm.lineCounts = null;
    vm.interval = apsDisplayEnums.CountDisplayInterval.CurrentHour;
    return countsService.getLineCounts(vm.selectedShop.id, vm.selectedPlant,
      apsDisplayEnums.CountDisplayInterval.Last24Hours, false).then(processData);
  }

  function loadState(): angular.IPromise<any> {
    if ($stateParams.reportPlantId && $stateParams.reportShopId
        && $stateParams.reportShopId !== $stateParams.shopId) {
      let plant = _.find(vm.plants, {id: $stateParams.reportPlantId});
      if (plant) {
        let shop = _.find(plant.shops, {id: $stateParams.reportShopId});
        if (shop) {
          vm.selectedPlant = plant;
          vm.selectedShop = shop;
        }
      }
    }
    return runReport();
  }

  function saveState(): void {
    let reportShopId = vm.selectedShop ? vm.selectedShop.id : undefined;
    if (reportShopId === $stateParams.shopId) {
      reportShopId = undefined;
    }
    let params: CountSummaryParams = {
      plantId: $stateParams.plantId,
      shopId: $stateParams.shopId,
      userId: $stateParams.userId,
      reportPlantId: (reportShopId && vm.selectedPlant) ? vm.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      autoRun: true
    };

    $state.transitionTo($state.current.name, params);
  }

  function generateReport(): void {
    if (vm.form.$invalid) {
      return;
    }

    saveState();
    loaderService.loadingPromise(runReport());
  }
  vm.generateReport = generateReport;

  function viewGroupRejects(dimpleEvent: {xValue: string}): void {
    if (!dimpleEvent || !dimpleEvent.xValue) {
      return;
    }

    let equipmentGroup = _.find(vm.lineCounts.equipmentGroups, { name: dimpleEvent.xValue });

    groupRejectsService.showGroupRejects( {
      equipmentGroup: equipmentGroup,
      shopId: vm.selectedShop.id,
      lineCounts: vm.lineCounts,
      countsInterval: vm.interval
    });
  }
  vm.viewGroupRejects = viewGroupRejects;

  function updatePlant(): void {
    vm.selectedShop = vm.selectedPlant.shops[0];
  }
  vm.updatePlant = updatePlant;
  vm.interval = apsDisplayEnums.CountDisplayInterval.Last24Hours;

  let promise = navigationService.getNavigation().then((nav: NavigationModel) => {
    vm.plants = nav.plants();
    vm.selectedPlant = nav.selectedPlant;
    vm.updatePlant();
    vm.selectedShop = nav.selectedShop;

    return loadState();
  });

  loaderService.loadingPromise(promise);

  return vm;
}


// WEBPACK FOOTER //
// ./src/app/controllers/counts/summary.controller.ts